import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { format, subMonths } from "date-fns";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

export const AnalyticsDashboard = () => {
  const { data: bookingStats, isLoading: isLoadingBookings } = useQuery({
    queryKey: ['host-booking-stats'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const startDate = subMonths(new Date(), 6);
      
      const { data, error } = await supabase
        .from('bookings')
        .select(`
          created_at,
          total_price,
          status,
          property_id,
          properties!inner(host_id)
        `)
        .eq('properties.host_id', user.id)
        .gte('created_at', startDate.toISOString());

      if (error) throw error;

      // Process monthly revenue data
      const monthlyData = data.reduce((acc: any, booking) => {
        const month = format(new Date(booking.created_at), 'MMM yyyy');
        if (!acc[month]) {
          acc[month] = { month, revenue: 0, bookings: 0 };
        }
        acc[month].revenue += Number(booking.total_price);
        acc[month].bookings += 1;
        return acc;
      }, {});

      // Process status distribution
      const statusData = data.reduce((acc: any, booking) => {
        if (!acc[booking.status]) {
          acc[booking.status] = { name: booking.status, value: 0 };
        }
        acc[booking.status].value += 1;
        return acc;
      }, {});

      return {
        monthly: Object.values(monthlyData),
        status: Object.values(statusData)
      };
    }
  });

  // Fetch recent reviews
  const { data: recentReviews, isLoading: isLoadingReviews } = useQuery({
    queryKey: ['host-recent-reviews'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from('reviews')
        .select(`
          *,
          properties!inner(host_id, title),
          profiles(full_name, profile_photo_url)
        `)
        .eq('properties.host_id', user.id)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      return data;
    }
  });

  if (isLoadingBookings || isLoadingReviews) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Monthly Revenue & Bookings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={bookingStats?.monthly}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="revenue"
                  stroke="#8884d8"
                  fill="#8884d8"
                  name="Revenue ($)"
                />
                <Area
                  yAxisId="right"
                  type="monotone"
                  dataKey="bookings"
                  stroke="#82ca9d"
                  fill="#82ca9d"
                  name="Bookings"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Booking Status Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={bookingStats?.status}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {bookingStats?.status.map((_: any, index: number) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent Reviews</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recentReviews?.map((review: any) => (
              <div key={review.id} className="flex items-start space-x-4 p-4 rounded-lg border">
                <Avatar>
                  <AvatarImage src={review.profiles?.profile_photo_url} />
                  <AvatarFallback>{review.profiles?.full_name?.[0] || 'G'}</AvatarFallback>
                </Avatar>
                <div className="flex-1 space-y-1">
                  <div className="flex items-center justify-between">
                    <p className="font-medium">{review.profiles?.full_name}</p>
                    <div className="flex items-center">
                      {Array.from({ length: review.rating }).map((_, i) => (
                        <svg
                          key={i}
                          className="w-4 h-4 text-yellow-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-gray-500">{review.properties?.title}</p>
                  <p className="text-sm">{review.comment}</p>
                  <p className="text-xs text-gray-500">
                    {format(new Date(review.created_at), 'MMM d, yyyy')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};